import React from 'react';
import { CreditCard, ArrowLeft, Info, Shield, Lock, FileText, CheckCircle2, Calendar, Clock } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { useUtilityRequestConfiguration } from '../../../../hooks';
import { getRemoteUtilityId } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

interface Props {
  onNext?: () => void;
  onPrevious?: () => void;
  allStepData?: any;
  onDataChange?: (data: any) => void;
  data?: any;
}

export const ReviewPayment: React.FC<Props> = ({ onNext, onPrevious, allStepData, data, onDataChange }) => {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'cash'>('online');
  const accountData = allStepData?.[0]?.accountData;
  const serviceRequestData = allStepData?.[2] || {};

  const { data: configData } = useUtilityRequestConfiguration({
    remote_utility_id: getRemoteUtilityId(),
    request_type: 'Service',
    disable_pagination: true,
  });

  const selectedServiceDetails = React.useMemo(() => {
    if (!configData?.result || !serviceRequestData?.serviceType) return null;
    const item = configData.result.find((r: any) => r.id?.toString() === serviceRequestData.serviceType);
    if (!item) return null;
    return {
      title: item.name || serviceRequestData.serviceTitle || '',
      desc: item.longDescription || item.long_description || '',
      price: item.extraData?.serviceCharge || item.extra_data?.service_charge || 0,
      category: item.supportRequestTypeDisplay || item.support_request_type_display || 'GENERAL',
    };
  }, [configData, serviceRequestData]);

  const getTimeSlotLabel = (slot: string) => {
    const map: Record<string, string> = {
      morning: 'Morning (8 AM – 12 PM)',
      afternoon: 'Afternoon (12 PM – 4 PM)',
      evening: 'Evening (4 PM – 8 PM)',
    };
    return map[slot] || slot;
  };

  const handleSubmit = () => {
    onDataChange?.({
      ...data,
      paymentMethod: 'Online Payment',
      serviceCharge: selectedServiceDetails?.price || 0,
    });
    onNext?.();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 space-y-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">{t("Review & Payment")}</h2>
          <p className="text-gray-500 text-sm mt-1">{t("Review your service request details and select payment method")}</p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <h3 className="font-semibold text-gray-900">{t("Service Information")}</h3>
            </div>
            
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t("Service Name")}</p>
                  <p className="font-medium text-gray-900">{selectedServiceDetails?.title}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t("Service Charges")}</p>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-900">AUD {selectedServiceDetails?.price}</p>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t("Base service fee")}</span>
                  </div>
                </div>
                <div className="col-span-full">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t("Description")}</p>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-xl border border-gray-100">
                    {selectedServiceDetails?.desc || t("No description available")}
                  </p>
                </div>
              </div>

              <div className="h-px bg-gray-100 my-6"></div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {t("Utility Service")}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{accountData?.utilityService?.join(', ') || t("NA")}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {t("Preferred Date")}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{serviceRequestData?.preferredDate || t("Not specified")}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {t("Preferred Time")}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{getTimeSlotLabel(serviceRequestData?.preferredTimeSlot) || t("Not specified")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                <CreditCard className="w-4 h-4" />
              </div>
              <h3 className="font-semibold text-gray-900">{t("Payment Method")}</h3>
            </div>
            
            <div className="p-5">
              <div className={`border-2 rounded-xl p-4 flex items-center gap-3 cursor-pointer transition-all ${paymentMethod === 'online' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200'}`} onClick={() => setPaymentMethod('online')}>
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className={`font-semibold ${paymentMethod === 'online' ? 'text-blue-900' : 'text-gray-900'}`}>{t("Online Payment")}</h4>
                  <p className={`text-xs mt-1 ${paymentMethod === 'online' ? 'text-blue-700' : 'text-gray-500'}`}>
                    {t("Secure payment processing with 256-bit SSL encryption")}
                  </p>
                </div>
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${paymentMethod === 'online' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>
                  {paymentMethod === 'online' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4">
          <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 text-gray-700 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button variant="default" type="button" onClick={handleSubmit}>
            {t("Pay & Submit")}
          </Button>
        </div>
      </div>

      <div className="w-full lg:w-[380px] space-y-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-4">{t("Order Summary")}</h3>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6">
            <p className="font-semibold text-gray-900 text-sm">{selectedServiceDetails?.title}</p>
            <p className="text-xs text-gray-500 mt-1">{selectedServiceDetails?.category}</p>
          </div>
          <div className="flex justify-between items-center text-lg font-bold text-gray-900 mt-2 mb-6">
            <span>{t("Total")}</span>
            <span>AUD {selectedServiceDetails?.price}</span>
          </div>
          <div className="bg-[#f4f7f9] rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-green-600" />
              <h4 className="font-bold text-gray-700 text-sm">{t("Secure & Verified")}</h4>
            </div>
            <ul className="text-xs text-gray-500 space-y-2 font-medium">
              <li>• {t("Account verified via OTP")}</li>
              <li>• {t("256-bit SSL encrypted payment")}</li>
              <li>• {t("Requests tracked with unique ID")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
